package se.mittpass.web.style

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.localStorage

enum class RosePineVariant(val key: String, val label: String, val iconClass: String) {
    DAWN("dawn", "Ljust", "fa-solid fa-sun"),
    MOON("moon", "Skymning", "fa-solid fa-cloud-moon"),
    MAIN("dark", "Mörkt", "fa-solid fa-moon");

    companion object {
        fun fromKey(key: String?): RosePineVariant = when (key?.lowercase()) {
            "moon" -> MOON
            "dark", "main" -> MAIN
            else -> DAWN
        }
    }
}

object ThemeManager {
    var currentVariant by mutableStateOf(RosePineVariant.DAWN)
        private set

    val isDark: Boolean get() = currentVariant != RosePineVariant.DAWN

    fun init() {
        val stored = localStorage.getItem("mittpass_theme")
        val variant = RosePineVariant.fromKey(stored)
        applyVariant(variant, persist = false)
    }

    fun setVariant(variant: RosePineVariant) {
        applyVariant(variant, persist = true)
    }

    fun toggleNext() {
        val next = when (currentVariant) {
            RosePineVariant.DAWN -> RosePineVariant.MOON
            RosePineVariant.MOON -> RosePineVariant.MAIN
            RosePineVariant.MAIN -> RosePineVariant.DAWN
        }
        setVariant(next)
    }

    private fun applyVariant(variant: RosePineVariant, persist: Boolean) {
        currentVariant = variant
        val root = document.documentElement ?: return
        when (variant) {
            RosePineVariant.DAWN -> root.removeAttribute("data-theme")
            RosePineVariant.MOON -> root.setAttribute("data-theme", "moon")
            RosePineVariant.MAIN -> root.setAttribute("data-theme", "dark")
        }
        if (persist) {
            localStorage.setItem("mittpass_theme", variant.key)
        }
    }
}

object RosePineTheme {
    const val base = "var(--rp-base)"
    const val surface = "var(--rp-surface)"
    const val overlay = "var(--rp-overlay)"
    const val text = "var(--rp-text)"
    const val subtle = "var(--rp-subtle)"
    const val muted = "var(--rp-muted)"
    const val pine = "var(--rp-pine)"
    const val foam = "var(--rp-foam)"
    const val iris = "var(--rp-iris)"
    const val gold = "var(--rp-gold)"
    const val rose = "var(--rp-rose)"
    const val love = "var(--rp-love)"
    const val highlightLow = "var(--rp-hl-low)"
    const val highlightMed = "var(--rp-hl-med)"
    const val highlightHigh = "var(--rp-hl-high)"

    const val ctaBg = "var(--rp-cta-bg)"
    const val ctaText = "var(--rp-cta-text)"
    const val ctaHover = "var(--rp-cta-hover)"

    const val okBg = "var(--rp-ok-bg)"
    const val okText = "var(--rp-ok-text)"
    const val okBorder = "var(--rp-ok-border)"

    const val warnBg = "var(--rp-warn-bg)"
    const val warnText = "var(--rp-warn-text)"
    const val warnBorder = "var(--rp-warn-border)"

    const val errBg = "var(--rp-err-bg)"
    const val errText = "var(--rp-err-text)"
    const val errBorder = "var(--rp-err-border)"

    const val infoBg = "var(--rp-info-bg)"
    const val infoText = "var(--rp-info-text)"
    const val infoBorder = "var(--rp-info-border)"

    fun tint(color: String, percent: Int = 14): String =
        "color-mix(in srgb, $color $percent%, var(--rp-surface))"
}

@androidx.compose.runtime.Composable
fun ThemeSwitcher(isCompact: Boolean = false) {
    val current = ThemeManager.currentVariant
    org.jetbrains.compose.web.dom.Div({
        classes("theme-switcher")
        attr("data-testid", "theme-switcher")
        attr("role", "group")
        attr("aria-label", "Färgtema")
        style {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "2px")
            property("padding", "2px")
            property("border-radius", "9999px")
            property("background-color", "var(--rp-overlay)")
            property("border", "1px solid var(--rp-hl-med)")
            property("flex-shrink", "0")
            property("box-sizing", "border-box")
        }
    }) {
        RosePineVariant.entries.forEach { variant ->
            val active = current == variant
            org.jetbrains.compose.web.dom.Button({
                if (active) {
                    classes("theme-switcher-btn", "active")
                } else {
                    classes("theme-switcher-btn")
                }
                attr("type", "button")
                attr("data-testid", "theme-btn-${variant.key}")
                attr("aria-pressed", active.toString())
                attr("title", "Byt färgtema till ${variant.label}")
                style {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("gap", if (isCompact) "0" else "0.3rem")
                    property("padding", if (isCompact) "0.3rem 0.45rem" else "0.32rem 0.58rem")
                    property("border-radius", "9999px")
                    property("border", "none")
                    property("background-color", if (active) "var(--rp-surface)" else "transparent")
                    property("color", if (active) "var(--rp-text)" else "var(--rp-muted)")
                    property("font-size", "0.75rem")
                    property("font-weight", if (active) "700" else "600")
                    property("cursor", "pointer")
                    property("white-space", "nowrap")
                    property("flex-shrink", "0")
                    property("box-shadow", if (active) "var(--rp-shadow-sm)" else "none")
                    property("transition", "all 0.15s ease")
                }
                onClick { ThemeManager.setVariant(variant) }
            }) {
                org.jetbrains.compose.web.dom.I({
                    classes(*variant.iconClass.split(" ").toTypedArray())
                    style { property("font-size", if (isCompact) "0.8rem" else "0.85rem") }
                })
                if (!isCompact) {
                    org.jetbrains.compose.web.dom.Span { org.jetbrains.compose.web.dom.Text(variant.label) }
                }
            }
        }
    }
}
